/**
 * Subtasks Service
 *
 * PE-769: Manages subtask data for breaking down tasks into smaller items
 * with completion tracking.
 */

import { createPersistedMockData } from './mockDataManager';

export interface Subtask {
  id: string;
  parentTaskId: string;
  title: string;
  description?: string;
  status: 'Open' | 'In Progress' | 'Completed';
  priority?: 'Low' | 'Medium' | 'High';
  assignedTo?: string;
  assignedToName?: string;
  dueDate?: string;
  completedAt?: string;
  completedBy?: string;
  completedByName?: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubtaskStats {
  total: number;
  completed: number;
  open: number;
  inProgress: number;
  percentage: number;
}

// Default subtasks for demonstration
const defaultSubtasks: Subtask[] = [];

// Storage using persistence pattern
export const persistedSubtasks = createPersistedMockData<Subtask>(
  'crm_subtasks',
  defaultSubtasks
);

/**
 * Get all subtasks for a parent task
 */
export const getSubtasksForTask = (parentTaskId: string): Subtask[] => {
  const allSubtasks = persistedSubtasks.get();
  return allSubtasks
    .filter(st => st.parentTaskId === parentTaskId)
    .sort((a, b) => {
      // Sort: Open first, then In Progress, then Completed
      const statusOrder = { 'Open': 0, 'In Progress': 1, 'Completed': 2 };
      const statusDiff = statusOrder[a.status] - statusOrder[b.status];
      if (statusDiff !== 0) return statusDiff;
      // Then by created date (newest first)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
};

/**
 * Get subtask completion statistics
 */
export const getSubtaskStats = (parentTaskId: string): SubtaskStats => {
  const subtasks = getSubtasksForTask(parentTaskId);
  const total = subtasks.length;
  const completed = subtasks.filter(st => st.status === 'Completed').length;
  const inProgress = subtasks.filter(st => st.status === 'In Progress').length;
  const open = subtasks.filter(st => st.status === 'Open').length;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return {
    total,
    completed,
    open,
    inProgress,
    percentage,
  };
};

/**
 * Get a single subtask by ID
 */
export const getSubtaskById = (subtaskId: string): Subtask | null => {
  const allSubtasks = persistedSubtasks.get();
  return allSubtasks.find(st => st.id === subtaskId) || null;
};

/**
 * Create a new subtask
 */
export const createSubtask = (
  subtask: Omit<Subtask, 'id' | 'createdAt' | 'updatedAt'>
): Subtask => {
  const allSubtasks = persistedSubtasks.get();

  const newSubtask: Subtask = {
    ...subtask,
    id: `subtask_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  persistedSubtasks.set([...allSubtasks, newSubtask]);

  return newSubtask;
};

/**
 * Update a subtask
 */
export const updateSubtask = (
  subtaskId: string,
  updates: Partial<Omit<Subtask, 'id' | 'parentTaskId' | 'createdAt' | 'createdBy' | 'createdByName'>>
): Subtask | null => {
  const allSubtasks = persistedSubtasks.get();
  const index = allSubtasks.findIndex(st => st.id === subtaskId);

  if (index === -1) return null;

  const updatedSubtask: Subtask = {
    ...allSubtasks[index],
    ...updates,
    updatedAt: new Date().toISOString(),
  };

  const newSubtasks = [...allSubtasks];
  newSubtasks[index] = updatedSubtask;
  persistedSubtasks.set(newSubtasks);

  return updatedSubtask;
};

/**
 * Mark subtask as complete
 */
export const completeSubtask = (
  subtaskId: string,
  userId: string,
  userName: string
): Subtask | null => {
  return updateSubtask(subtaskId, {
    status: 'Completed',
    completedAt: new Date().toISOString(),
    completedBy: userId,
    completedByName: userName,
  });
};

/**
 * Reopen a completed subtask
 */
export const reopenSubtask = (subtaskId: string): Subtask | null => {
  return updateSubtask(subtaskId, {
    status: 'Open',
    completedAt: undefined,
    completedBy: undefined,
    completedByName: undefined,
  });
};

/**
 * Delete a subtask
 */
export const deleteSubtask = (subtaskId: string): boolean => {
  const allSubtasks = persistedSubtasks.get();
  const filtered = allSubtasks.filter(st => st.id !== subtaskId);

  if (filtered.length === allSubtasks.length) return false;

  persistedSubtasks.set(filtered);
  return true;
};

/**
 * Delete all subtasks for a parent task (when parent is deleted)
 */
export const deleteSubtasksForTask = (parentTaskId: string): number => {
  const allSubtasks = persistedSubtasks.get();
  const filtered = allSubtasks.filter(st => st.parentTaskId !== parentTaskId);
  const deletedCount = allSubtasks.length - filtered.length;

  persistedSubtasks.set(filtered);
  return deletedCount;
};

/**
 * Get all subtasks (for admin/debugging)
 */
export const getAllSubtasks = (): Subtask[] => {
  return persistedSubtasks.get();
};

/**
 * Check if a task has any subtasks
 */
export const taskHasSubtasks = (parentTaskId: string): boolean => {
  const allSubtasks = persistedSubtasks.get();
  return allSubtasks.some(st => st.parentTaskId === parentTaskId);
};
